"use client";

import { Brain, Heart, Waves, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ServiceCapabilities() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const quote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    number: "01",
    title: "Siemens Integration",
    description: "We specialize in the seamless integration of Siemens Sinumerik systems. Our technical precision ensures that your CNC machinery operates with the exact logic and reliability required for high-output industrial environments.",
    icon: Brain,
    offset: "lg:mt-0"
  }, {
    number: "02",
    title: "System Retrofitting",
    description: "Breathe new life into legacy hardware. By upgrading outdated controls with modern Siemens interfaces, we restore operational efficiency and extend the lifecycle of your most critical industrial assets.",
    icon: Heart,
    offset: "lg:mt-20"
  }, {
    number: "03",
    title: "Field Diagnostics",
    description: "Our on-site technical team utilizes advanced diagnostic protocols to identify and resolve system failures. We minimize downtime through rapid response and precise mechanical troubleshooting across Gauteng.",
    icon: Waves,
    offset: "lg:mt-0"
  }];
  return <section data-section-id="938" id="service-capabilities" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-40">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-10" style={{
      background: "radial-gradient(circle at 50% 30%, hsl(183, 100%, 32%, 0.15) 0%, transparent 60%)"
    }} />

      <div className="relative container mx-auto px-6">
        {}
        <div ref={intro.ref} className={`mx-auto max-w-[720px] text-center transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary">Industrial Automation</p>
          <h2 className="font-default font-bold text-foreground mt-8 leading-[1.1]" style={{
          fontSize: "clamp(3rem, 6vw, 5rem)"
        }}>
            Precision
            <br />
            in <em className="italic font-normal text-primary">motion</em>
          </h2>
          <p className="font-default text-foreground/80 mt-10 text-[19px] leading-relaxed">IMCNC Designs provides the architectural force behind industrial automation. Specializing in Siemens parts and services, we deliver the binary precision required for modern manufacturing excellence.</p>
        </div>

        {}
        <div className="mt-[100px] grid grid-cols-1 gap-16 md:gap-20 lg:grid-cols-3 lg:gap-12">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          const card = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <div data-index={i} ref={card.ref} key={feature.number} className={`flex flex-col ${feature.offset} transition-all duration-1000 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Icon className="text-primary" strokeWidth={1.5} size={60} aria-hidden="true" />
                <p className="font-mono text-xs tracking-[0.2em] text-primary mt-10">
                  {feature.number}
                </p>
                <h3 className="font-default font-semibold text-foreground mt-4 text-[20px] uppercase tracking-wider leading-tight">
                  {feature.title}
                </h3>
                <p className="font-default text-foreground/70 mt-5 text-[15px] leading-relaxed">
                  {feature.description}
                </p>
                <Separator className="bg-primary/20 mt-10" />
                
              </div>;
        })}

          {}
          <div data-index={3} className="lg:col-span-3 mt-12 lg:mt-24">
            <FourthFeature />
          </div>
        </div>

        {}
        <div ref={quote.ref} className={`mx-auto mt-32 max-w-[1000px] text-center md:mt-40 transition-all duration-1000 ease-out ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <blockquote className="font-default italic font-light text-foreground leading-[1.15]" style={{
          fontSize: "clamp(2rem, 4vw, 3rem)"
        }}>
            Engineering is the language
            <br />
            your production line
            <br />
            already speaks.
          </blockquote>
          <p className="font-mono text-foreground/60 mt-10 text-xs uppercase tracking-[0.3em]">IMCNC Designs</p>
        </div>
      </div>
    </section>;
}
function FourthFeature() {
  const card = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={card.ref} className={`mx-auto grid max-w-5xl grid-cols-1 items-start gap-10 md:grid-cols-12 md:gap-14 transition-all duration-1000 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="md:col-span-4 flex justify-start md:justify-center">
        <Sparkles className="text-primary" strokeWidth={1.5} size={60} aria-hidden="true" />
      </div>
      <div className="md:col-span-8 flex flex-col">
        <p className="font-mono text-xs tracking-[0.2em] text-primary">04</p>
        <h3 className="font-default font-semibold text-foreground mt-4 text-[28px] leading-tight md:text-[32px] uppercase tracking-wider">
          Spare Parts Inventory
        </h3>
        <p className="font-default text-foreground/70 mt-5 text-[15px] leading-relaxed max-w-2xl">
          We maintain a comprehensive stock of genuine Siemens components, from power supplies and drive modules to specialized CNC controllers. Our logistics network ensures that critical parts reach your facility in Gauteng and beyond with minimal lead times, keeping your operations in constant motion.
        </p>
        <Separator className="bg-primary/20 mt-10" />
        <Link to="/PartsCataloguePage" className="font-mono text-foreground hover:text-primary mt-5 inline-block text-xs uppercase tracking-[0.25em] transition-colors">Inventory List</Link>
      </div>
    </div>;
}